import { Badge, Button, Col, Container, Image, Row, Spinner } from 'react-bootstrap';
import { FaArrowRightLong, FaGlobe, FaPeopleGroup, FaRegHandshake } from 'react-icons/fa6';
import { useServicesPage } from '@/hooks/useServicesPage';
import { toImageSrc } from '@/utils/imageUrl';
import heroImage from '@/assets/images/category/hotel/resort/02.jpg';

const Hero = ({ sectionData: propSectionData }) => {
  const heroImageSrc = toImageSrc(heroImage, '');

  const { section: fetchedSection, loading } = useServicesPage('hero');
  const section = propSectionData || fetchedSection;
  
  // Calculate background class and effects
  const bgColor = section?.background_color || 'light';
  const bgEffect = section?.background_effect || null;
  let bgClass = bgColor === 'primary' ? 'section-bg-primary' : bgColor === 'white' ? 'bg-white' : 'bg-light';
  // Apply gradient effect if selected
  if (bgColor === 'primary' && bgEffect === 'gradient') {
    bgClass = 'section-bg-primary-gradient';
  }
  const hasDotsEffect = bgColor === 'primary' && bgEffect === 'dots';
  
  const defaultButtons = [
    {
      label: 'Services for Tour Operators',
      url: '#tour-operator-services',
      IconComponent: FaArrowRightLong,
      color: 'primary',
      variant: 'solid'
    },
    {
      label: 'Groups & MICE',
      url: '#groups-mice',
      IconComponent: null,
      color: 'primary',
      variant: 'outline'
    }
  ];
  const sectionButtons = section?.content?.buttons?.length ? section.content.buttons : defaultButtons;
  const renderButtons = (buttons) => (
    <div className="d-flex flex-wrap gap-3 mb-4">
      {buttons.map((button, idx) => {
        const color = button.color || 'primary';
        const variant = button.variant === 'outline' ? `outline-${color}` : color;
        const Icon = button.IconComponent;
        return (
          <Button key={idx} href={button.url} size="lg" variant={variant}>
            {button.label}
            {Icon && <Icon className="ms-2" />}
          </Button>
        );
      })}
    </div>
  );

  if (loading && !propSectionData) {
    return (
      <section className={`py-5 py-lg-6 ${bgClass} services-section services-hero ${hasDotsEffect ? 'position-relative overflow-hidden' : ''}`}>
        <Container>
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
          </div>
        </Container>
      </section>
    );
  }
  
  if (!section || !section.is_active) {
    // Fallback to default content if section not found
    return (
      <section className={`py-5 py-lg-6 ${bgClass} services-section services-hero`}>
        <Container>
          <Row className="align-items-center g-4 g-xl-5">
            <Col lg={6}>
              <Badge bg="primary" className="bg-opacity-10 text-primary">
                Destination Management • East Crete
              </Badge>
              <h1 className="sec-title mb-3">
                Destination Management in East Crete.
              </h1>
              <p className="lead intro-text">
                For more than four decades we support tour operators, groups and partners with reliable services,
                deep local knowledge and genuine Cretan hospitality. We are your on-the-ground team in East Crete.
              </p>
              {renderButtons(defaultButtons)}
            </Col>
            <Col lg={6}>
              <div className="position-relative rounded-4 overflow-hidden shadow-lg">
                <Image src={heroImageSrc} alt="East Crete coastline" className="w-100 h-100 object-fit-cover" />
                {/* Fallback overlay if no database items */}
                <div className="services-hero-overlay-badge">
                  <p className="services-hero-overlay-description mb-2">Trusted by leading European tour operators</p>
                  <h4 className="services-hero-overlay-title mb-0">Season 2024/25 programs now open</h4>
                </div>
              </div>
              {/* Fallback feature cards if no database items */}
              <Row className="g-3 g-md-4 mt-4">
                <Col sm={6}>
                  <div className="services-hero-feature-card h-100">
                    <div className="services-hero-feature-card-content">
                      <div className="services-hero-feature-icon bg-primary bg-opacity-10 text-primary">
                        <FaGlobe size={20} />
                      </div>
                      <div className="services-hero-feature-text">
                        <h6 className="services-hero-feature-title mb-1">40+ years</h6>
                        <p className="services-hero-feature-description mb-0">Independent, local DMC</p>
                      </div>
                    </div>
                  </div>
                </Col>
                <Col sm={6}>
                  <div className="services-hero-feature-card h-100">
                    <div className="services-hero-feature-card-content">
                      <div className="services-hero-feature-icon bg-success bg-opacity-10 text-success">
                        <FaRegHandshake size={20} />
                      </div>
                      <div className="services-hero-feature-text">
                        <h6 className="services-hero-feature-title mb-1">Partner-first</h6>
                        <p className="services-hero-feature-description mb-0">Transparent &amp; flexible collaboration</p>
                      </div>
                    </div>
                  </div>
                </Col>
                <Col sm={6}>
                  <div className="services-hero-feature-card h-100">
                    <div className="services-hero-feature-card-content">
                      <div className="services-hero-feature-icon bg-info bg-opacity-10 text-info">
                        <FaPeopleGroup size={20} />
                      </div>
                      <div className="services-hero-feature-text">
                        <h6 className="services-hero-feature-title mb-1">End-to-end care</h6>
                        <p className="services-hero-feature-description mb-0">Hotel contracting to in-resort support</p>
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </section>
    );
  }
  
  return (
    <section className={`py-5 py-lg-6 ${bgClass} services-section services-hero ${hasDotsEffect ? 'position-relative overflow-hidden' : ''}`}>
      {hasDotsEffect && (
        <>
          <span className="section-bg-dots section-bg-dots--left d-none d-xl-block" />
          <span className="section-bg-dots section-bg-dots--right d-none d-lg-block" />
        </>
      )}
      <Container className={hasDotsEffect ? 'position-relative' : ''}>
        <Row className="align-items-center g-4 g-xl-5">
          <Col lg={6}>
            {section.subtitle && (
              <Badge bg="primary" className="bg-opacity-10 text-primary">
                {section.subtitle}
              </Badge>
            )}
            {section.title && (
              <h1 className="sec-title mb-3">
                {section.title}
              </h1>
            )}
            {section.intro_text && (
              <p className="lead intro-text" dangerouslySetInnerHTML={{ __html: section.intro_text }} />
            )}
            {renderButtons(sectionButtons)}
          </Col>
          <Col lg={6}>
            <div className="position-relative rounded-4 overflow-hidden shadow-lg">
              <Image src={heroImageSrc} alt="East Crete coastline" className="w-100 h-100 object-fit-cover" />
              {/* Overlay item (Season 2024/25) */}
              {section.items && section.items.find(item => (item.item_type === 'overlay' || item.content?.overlay) && item.is_active) && (() => {
                const overlayItem = section.items.find(item => (item.item_type === 'overlay' || item.content?.overlay) && item.is_active);
                const Icon = overlayItem?.IconComponent;
                return (
                  <div className="services-hero-overlay-badge">
                    {overlayItem.description && (
                      <p className="services-hero-overlay-description mb-2" dangerouslySetInnerHTML={{ __html: overlayItem.description }} />
                    )}
                    {overlayItem.title && (
                      <div className="d-flex align-items-center gap-2">
                        {Icon && (
                          <Icon 
                            className={`services-hero-overlay-icon text-${overlayItem.icon_color || 'primary'}`}
                            style={{ fontSize: '1.5rem', flexShrink: 0 }}
                          />
                        )}
                        <h4 className="services-hero-overlay-title mb-0">{overlayItem.title}</h4>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
            {/* Feature cards */}
            {section.items && section.items.filter(item => item.item_type === 'card' && item.is_active).length > 0 && (
              <Row className="g-3 g-md-4 mt-3 services-hero-feature-cards-row">
                {section.items
                  .filter(item => item.item_type === 'card' && item.is_active)
                  .sort((a, b) => (a.display_order || 0) - (b.display_order || 0))
                  .map((item, idx) => {
                    const Icon = item.IconComponent;
                    const colorClass = item.icon_color === 'primary' ? 'primary' : 
                                     item.icon_color === 'success' ? 'success' : 
                                     item.icon_color === 'info' ? 'info' : 
                                     item.icon_color === 'warning' ? 'warning' :
                                     item.icon_color === 'danger' ? 'danger' :
                                     item.icon_color === 'secondary' ? 'secondary' :
                                     'primary';
                    return (
                      <Col sm={6} key={item.id || idx}>
                        <div className="services-hero-feature-card h-100">
                          <div className="services-hero-feature-card-content">
                            {Icon && (
                              <div className={`services-hero-feature-icon bg-${colorClass} bg-opacity-10 text-${colorClass}`}>
                                <Icon size={20} />
                              </div>
                            )}
                            <div className="services-hero-feature-text">
                              {item.title && <h6 className="services-hero-feature-title mb-1">{item.title}</h6>}
                              {item.description && (
                                <p className="services-hero-feature-description mb-0" dangerouslySetInnerHTML={{ __html: item.description }} />
                              )}
                            </div>
                          </div>
                        </div>
                      </Col>
                    );
                  })}
              </Row>
            )}
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default Hero;
